"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { buildNewsMediaUrl } from "@/utils/newsMediaUrl";
import styles from "./news.module.css";

interface Item {
  _id: string;
  title: string;
  summary?: string;
  image?: string;
  category?: string;
  author?: string;
  slug?: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function FeaturedCard({ item }: { item: Item }) {
  const rawSlug = item.slug || slugify(item.title);
  const encodedSlug = encodeURIComponent(rawSlug);
  const logoPath = "/sdtc.webp";
  
  const [imgSrc, setImgSrc] = useState(
    item.image ? buildNewsMediaUrl(item.image, logoPath) : logoPath
  );
  const [useFallback, setUseFallback] = useState(false);

  return (
    <Link
      href={`/tin-tuc/${encodedSlug}`}
      className={styles.featuredCard}
    >
      <div className={styles.imageWrapper}>
        {useFallback ? (
          <img
            src={logoPath}
            alt={item.title}
            className={styles.featuredImage}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = logoPath;
            }}
          />
        ) : (
          <Image
            src={imgSrc}
            alt={item.title}
            fill
            className={styles.featuredImage}
            priority
            sizes="(max-width: 768px) 100%, (max-width: 1200px) 50vw, 33vw"
            unoptimized={
              item.image?.startsWith('http://localhost:7001') || 
              item.image?.startsWith('http://127.0.0.1:7001') ||
              item.image?.startsWith('http://192.168') ||
              item.image?.startsWith('http://10.') ||
              item.image?.startsWith('http://172.')
            }
            onError={() => {
              setUseFallback(true);
              setImgSrc(logoPath);
            }}
            onLoadingComplete={(result) => {
              if (result.naturalWidth === 0) {
                setUseFallback(true);
                setImgSrc(logoPath);
              }
            }}
          />
        )}
      </div>

      <div className={styles.overlay}></div>

      <div className={styles.content}>
        {item.category && (
          <span className={styles.category}>{item.category}</span>
        )}
        <h3 className={styles.title}>{item.title}</h3>
        {item.summary && (
          <p className={styles.summary}>{item.summary}</p>
        )}
        {item.author && (
          <div className={styles.featuredAuthor}>Tác giả: {item.author}</div>
        )}
      </div>
    </Link>
  );
}

export default function FeaturedList({ items }: { items: Item[] }) {
  if (!items || items.length === 0) return null;

  return (
    <section className={styles.featuredSection}>
      <div className={styles.featuredList}>
        {items.map((it) => (
          <FeaturedCard key={it._id} item={it} />
        ))}
      </div>
    </section>
  );
}